import { useState, useEffect, useCallback } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import OwnerSidebar from "@/components/navigation/OwnerSidebar";
import AppFooter from "@/components/layout/AppFooter";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { 
  Users, ChevronLeft, Loader2, UserX, UserCheck, Mail, Phone, Shield, ShieldOff
} from "lucide-react";
import axios from "axios";

const API_URL = process.env.REACT_APP_BACKEND_URL || "http://localhost:8001";

const TeamPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  
  const [dienstplan, setDienstplan] = useState(null);
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [blockingUser, setBlockingUser] = useState(null);
  const [coAdminUpdatingUser, setCoAdminUpdatingUser] = useState(null);

  const canManageCoAdmins = Boolean(dienstplan?.is_owner);
  const getRoleLabel = (member) => {
    if (member.id === dienstplan?.owner_id || member.role === "owner") return "Besitzer";
    if (member.is_superadmin) return "Superadmin";
    if (member.is_coadmin) return "Co-Admin";
    return "Mitarbeiter";
  };
  const getRoleBadge = (member) => {
    const roleLabel = getRoleLabel(member);
    if (roleLabel === "Besitzer") {
      return <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200">Besitzer</Badge>;
    }
    if (roleLabel === "Superadmin") {
      return <Badge className="bg-indigo-50 text-indigo-700 border-indigo-200">Superadmin</Badge>;
    }
    if (roleLabel === "Co-Admin") {
      return <Badge className="bg-blue-50 text-blue-700 border-blue-200">Co-Admin</Badge>;
    }
    return <Badge className="bg-gray-50 text-gray-600 border-gray-200">Mitarbeiter</Badge>;
  };
  const canManageMember = (member) => {
    if (!member) return false;
    if (member.id === user?.id) return false;
    if (member.id === dienstplan?.owner_id || member.role === "owner") return false;
    if (member.is_superadmin) return false;
    return true;
  };

  const fetchData = useCallback(async () => {
    setLoading(true);

    try {
      const [dpRes, membersRes] = await Promise.all([
        axios.get(`${API_URL}/api/dienstplan/${id}`),
        axios.get(`${API_URL}/api/dienstplan/${id}/members`)
      ]);
      setDienstplan(dpRes.data);
      setMembers(membersRes.data);
    } catch (error) {
      console.error("Error fetching data:", error);
      if (error.response?.status === 404) {
        toast.error("Dienstplan nicht gefunden");
        navigate("/dienstplan");
      } else {
        toast.error("Fehler beim Laden der Daten");
      }
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleBlockUser = async (userId, shouldBlock) => {
    setBlockingUser(userId);
    try {
      await axios.post(`${API_URL}/api/dienstplan/${id}/block-member`, {
        user_id: userId,
        is_blocked: shouldBlock
      });
      
      setMembers(members.map(m => 
        m.id === userId ? { ...m, is_blocked: shouldBlock } : m
      ));
      
      toast.success(shouldBlock ? "Mitarbeiter blockiert" : "Mitarbeiter entsperrt");
    } catch (error) {
      console.error("Error blocking user:", error);
      toast.error("Fehler beim Blockieren/Entsperren");
    } finally {
      setBlockingUser(null);
    }
  };

  const handleLogout = () => {
    logout();
    navigate("/");
    toast.success("Erfolgreich abgemeldet");
  };

  const handleToggleCoAdmin = async (member) => {
    setCoAdminUpdatingUser(member.id);
    try {
      const nextValue = !Boolean(member.is_coadmin);
      await axios.put(`${API_URL}/api/dienstplan/${id}/member/${member.id}/coadmin`, {
        is_coadmin: nextValue,
      });

      setMembers((prev) => prev.map((m) => (
        m.id === member.id ? { ...m, is_coadmin: nextValue } : m
      )));
      toast.success(nextValue ? "Co-Admin aktiviert" : "Co-Admin entfernt");
    } catch (error) {
      console.error("Error updating co-admin:", error);
      toast.error("Co-Admin konnte nicht geändert werden");
    } finally {
      setCoAdminUpdatingUser(null);
    }
  };

  const visibleMembers = members.filter((m) => m.id !== user?.id);
  const activeMembers = visibleMembers.filter((m) => !m.is_blocked);
  const blockedMembers = visibleMembers.filter((m) => m.is_blocked);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#f8fafc] flex flex-col">
        <div className="flex-1 flex items-center justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-[#17c671]" />
        </div>
        <AppFooter />
      </div>
    );
  }

  return (
    <div className="dashboard-layout" data-testid="team-page">
      {/* Mobile Sidebar Overlay */}
      <div 
        className={`sidebar-overlay ${sidebarOpen ? 'open' : ''}`}
        onClick={() => setSidebarOpen(false)}
      />
      
      <OwnerSidebar
        user={user}
        sidebarOpen={sidebarOpen}
        activeMain="dashboard"
        currentPlanId={id}
        currentPlanSection="team"
        onLogout={handleLogout}
      />

      {/* Main Content - Light */}
      <main className="dashboard-main">
        <div className="max-w-6xl mx-auto px-6 py-8 flex-1 w-full">
          <div className="mb-6 flex items-center gap-3">
            <Link to={`/dienstplan/${id}`} className="text-gray-500 hover:text-gray-700">
              <ChevronLeft className="w-5 h-5" />
            </Link>
            <h1 className="font-heading text-2xl font-semibold text-[#212529]">Personal</h1>
          </div>
          {/* Stats */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            <Card className="stat-card animate-fade-in stagger-1">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-[#17c671]/10 rounded-lg flex items-center justify-center">
                  <Users className="w-6 h-6 text-[#17c671]" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-[#212529]">{visibleMembers.length}</p>
                  <p className="text-sm text-gray-500">Personal gesamt</p>
                </div>
              </div>
            </Card>
            <Card className="stat-card animate-fade-in stagger-2">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-[#17c671]/10 rounded-lg flex items-center justify-center">
                  <UserCheck className="w-6 h-6 text-[#17c671]" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-[#212529]">{activeMembers.length}</p>
                  <p className="text-sm text-gray-500">Aktiv</p>
                </div>
              </div>
            </Card>
            <Card className="stat-card animate-fade-in stagger-3">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-red-50 rounded-lg flex items-center justify-center">
                  <UserX className="w-6 h-6 text-red-500" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-[#212529]">{blockedMembers.length}</p>
                  <p className="text-sm text-gray-500">Blockiert</p>
                </div>
              </div>
            </Card>
          </div>
          
          {/* Active Members */}
          <div className="mb-8">
            <h2 className="font-heading text-xl font-semibold text-[#212529] mb-4 flex items-center gap-2">
              <UserCheck className="w-5 h-5 text-[#17c671]" />
              Aktive Mitglieder ({activeMembers.length})
            </h2>
            
            {activeMembers.length === 0 ? (
              <Card className="border-gray-200">
                <CardContent className="py-12 text-center">
                  <Users className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                  <p className="text-gray-500">Noch keine Mitglieder. Lade dein Team über den Einladungslink ein.</p>
                  <Button 
                    className="mt-4 bg-[#17c671] hover:bg-[#14a85d] text-white"
                    onClick={() => navigate(`/dienstplan/${id}`)}
                  >
                    Zum Dienstplan
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <Card className="border-gray-200">
                <CardContent className="p-0">
                  <div className="hidden md:block overflow-x-auto">
                    <table className="w-full" data-testid="active-members-table">
                      <thead>
                        <tr className="border-b border-gray-100 bg-gray-50">
                          <th className="text-left px-4 py-3 text-sm font-semibold text-gray-600">Name</th>
                          <th className="text-left px-4 py-3 text-sm font-semibold text-gray-600">E-Mail</th>
                          <th className="text-left px-4 py-3 text-sm font-semibold text-gray-600">Telefonnummer</th>
                          <th className="text-left px-4 py-3 text-sm font-semibold text-gray-600">Rolle</th>
                          <th className="text-left px-4 py-3 text-sm font-semibold text-gray-600">Status</th>
                          <th className="text-right px-4 py-3 text-sm font-semibold text-gray-600">Aktionen</th>
                        </tr>
                      </thead>
                      <tbody>
                        {activeMembers.map((member) => (
                          <tr key={member.id} className="border-b border-gray-50 hover:bg-gray-50" data-testid={`member-row-${member.id}`}>
                            <td className="px-4 py-3">
                              <div className="flex items-center gap-3">
                                <div className="w-9 h-9 bg-[#17c671]/10 rounded-full flex items-center justify-center">
                                  <span className="text-sm font-medium text-[#17c671]">
                                    {member.name.charAt(0).toUpperCase()}
                                  </span>
                                </div>
                                <span className="font-medium text-[#212529]">{member.name}</span>
                              </div>
                            </td>
                            <td className="px-4 py-3">
                              <div className="flex items-center gap-2 text-gray-600">
                                <Mail className="w-4 h-4" />
                                {member.email}
                              </div>
                            </td>
                            <td className="px-4 py-3">
                              <div className="flex items-center gap-2 text-gray-600">
                                <Phone className="w-4 h-4" />
                                {member.phone || "-"}
                              </div>
                            </td>
                            <td className="px-4 py-3">
                              {getRoleBadge(member)}
                            </td>
                            <td className="px-4 py-3">
                              <Badge className="bg-[#17c671]/10 text-[#17c671] border-[#17c671]/30">
                                <Shield className="w-3 h-3 mr-1" />
                                Aktiv
                              </Badge>
                            </td>
                            <td className="px-4 py-3 text-right">
                              <div className="flex flex-col items-end gap-1.5">
                                {canManageCoAdmins ? (
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    className="text-blue-600 border-blue-200 hover:bg-blue-50 hover:text-blue-700"
                                    onClick={() => handleToggleCoAdmin(member)}
                                    disabled={coAdminUpdatingUser === member.id || !canManageMember(member)}
                                    data-testid={`coadmin-btn-${member.id}`}
                                  >
                                    {coAdminUpdatingUser === member.id ? (
                                      <Loader2 className="w-4 h-4 animate-spin" />
                                    ) : member.is_coadmin ? (
                                      "Co-Admin entfernen"
                                    ) : (
                                      "Zu Co-Admin machen"
                                    )}
                                  </Button>
                                ) : null}
                                <Button
                                  variant="outline"
                                  size="sm"
                                  className="text-red-500 border-red-200 hover:bg-red-50 hover:text-red-600"
                                  onClick={() => handleBlockUser(member.id, true)}
                                  disabled={blockingUser === member.id || !canManageMember(member)}
                                  data-testid={`block-btn-${member.id}`}
                                >
                                  {blockingUser === member.id ? (
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                  ) : (
                                    <>
                                      <ShieldOff className="w-4 h-4 mr-1" />
                                      Blockieren
                                    </>
                                  )}
                                </Button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <div className="md:hidden p-3 space-y-3">
                    {activeMembers.map((member) => (
                      <div key={member.id} className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm" data-testid={`member-mobile-card-${member.id}`}>
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex items-center gap-3 min-w-0">
                            <div className="w-10 h-10 bg-[#17c671]/10 rounded-full flex items-center justify-center flex-shrink-0">
                              <span className="text-sm font-medium text-[#17c671]">
                                {member.name.charAt(0).toUpperCase()}
                              </span>
                            </div>
                            <div className="min-w-0">
                              <p className="font-medium text-[#212529] truncate">{member.name}</p>
                              <p className="text-sm text-gray-500 truncate inline-flex items-center gap-1">
                                <Mail className="w-3.5 h-3.5" />
                                {member.email}
                              </p>
                              <p className="text-sm text-gray-500 truncate inline-flex items-center gap-1 mt-1">
                                <Phone className="w-3.5 h-3.5" />
                                {member.phone || "-"}
                              </p>
                              <p className="text-sm mt-1">
                                <span className={getRoleLabel(member) === "Besitzer" ? "text-emerald-700" : getRoleLabel(member) === "Superadmin" ? "text-indigo-700" : getRoleLabel(member) === "Co-Admin" ? "text-blue-700" : "text-gray-500"}>
                                  {getRoleLabel(member)}
                                </span>
                              </p>
                            </div>
                          </div>
                          <Badge className="bg-[#17c671]/10 text-[#17c671] border-[#17c671]/30 whitespace-nowrap">
                            <Shield className="w-3 h-3 mr-1" />
                            Aktiv
                          </Badge>
                        </div>
                        <div className="mt-3 flex justify-end gap-2">
                          {canManageCoAdmins ? (
                            <Button
                              variant="outline"
                              size="sm"
                              className="text-blue-600 border-blue-200 hover:bg-blue-50 hover:text-blue-700"
                              onClick={() => handleToggleCoAdmin(member)}
                              disabled={coAdminUpdatingUser === member.id || !canManageMember(member)}
                              data-testid={`coadmin-btn-mobile-${member.id}`}
                            >
                              {coAdminUpdatingUser === member.id ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                              ) : member.is_coadmin ? (
                                "Co-Admin entfernen"
                              ) : (
                                "Co-Admin"
                              )}
                            </Button>
                          ) : null}
                          <Button
                            variant="outline"
                            size="sm"
                            className="text-red-500 border-red-200 hover:bg-red-50 hover:text-red-600"
                            onClick={() => handleBlockUser(member.id, true)}
                            disabled={blockingUser === member.id || !canManageMember(member)}
                            data-testid={`block-btn-mobile-${member.id}`}
                          >
                            {blockingUser === member.id ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <>
                                <ShieldOff className="w-4 h-4 mr-1" />
                                Blockieren
                              </>
                            )}
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
          
          {/* Blocked Members */}
          {blockedMembers.length > 0 && (
            <div>
              <h2 className="font-heading text-xl font-semibold text-[#212529] mb-4 flex items-center gap-2">
                <UserX className="w-5 h-5 text-red-500" />
                Blockierte Mitglieder ({blockedMembers.length})
              </h2>
              
              <Card className="border-gray-200 border-red-100">
                <CardContent className="p-0">
                  <div className="hidden md:block overflow-x-auto">
                    <table className="w-full" data-testid="blocked-members-table">
                      <thead>
                        <tr className="border-b border-gray-100 bg-red-50/50">
                          <th className="text-left px-6 py-4 text-sm font-semibold text-gray-600">Name</th>
                          <th className="text-left px-6 py-4 text-sm font-semibold text-gray-600">E-Mail</th>
                          <th className="text-left px-6 py-4 text-sm font-semibold text-gray-600">Telefonnummer</th>
                          <th className="text-left px-6 py-4 text-sm font-semibold text-gray-600">Rolle</th>
                          <th className="text-left px-6 py-4 text-sm font-semibold text-gray-600">Status</th>
                          <th className="text-right px-6 py-4 text-sm font-semibold text-gray-600">Aktionen</th>
                        </tr>
                      </thead>
                      <tbody>
                        {blockedMembers.map((member) => (
                          <tr key={member.id} className="border-b border-gray-50 hover:bg-gray-50" data-testid={`blocked-member-row-${member.id}`}>
                            <td className="px-6 py-4">
                              <div className="flex items-center gap-3">
                                <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
                                  <span className="text-sm font-medium text-red-500">
                                    {member.name.charAt(0).toUpperCase()}
                                  </span>
                                </div>
                                <span className="font-medium text-gray-400">{member.name}</span>
                              </div>
                            </td>
                            <td className="px-6 py-4">
                              <div className="flex items-center gap-2 text-gray-400">
                                <Mail className="w-4 h-4" />
                                {member.email}
                              </div>
                            </td>
                            <td className="px-6 py-4">
                              <div className="flex items-center gap-2 text-gray-400">
                                <Phone className="w-4 h-4" />
                                {member.phone || "-"}
                              </div>
                            </td>
                            <td className="px-6 py-4">
                              {getRoleBadge(member)}
                            </td>
                            <td className="px-6 py-4">
                              <Badge className="bg-red-50 text-red-500 border-red-200">
                                <ShieldOff className="w-3 h-3 mr-1" />
                                Blockiert
                              </Badge>
                            </td>
                            <td className="px-6 py-4 text-right">
                              <div className="flex flex-col items-end gap-2">
                                {canManageCoAdmins ? (
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    className="text-blue-600 border-blue-200 hover:bg-blue-50 hover:text-blue-700"
                                    onClick={() => handleToggleCoAdmin(member)}
                                    disabled={coAdminUpdatingUser === member.id || !canManageMember(member)}
                                    data-testid={`coadmin-blocked-btn-${member.id}`}
                                  >
                                    {coAdminUpdatingUser === member.id ? (
                                      <Loader2 className="w-4 h-4 animate-spin" />
                                    ) : member.is_coadmin ? (
                                      "Co-Admin entfernen"
                                    ) : (
                                      "Zu Co-Admin machen"
                                    )}
                                  </Button>
                                ) : null}
                                <Button
                                  variant="outline"
                                  size="sm"
                                  className="text-[#17c671] border-[#17c671]/30 hover:bg-[#17c671]/10"
                                  onClick={() => handleBlockUser(member.id, false)}
                                  disabled={blockingUser === member.id || !canManageMember(member)}
                                  data-testid={`unblock-btn-${member.id}`}
                                >
                                  {blockingUser === member.id ? (
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                  ) : (
                                    <>
                                      <Shield className="w-4 h-4 mr-1" />
                                      Entsperren
                                    </>
                                  )}
                                </Button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <div className="md:hidden p-3 space-y-3">
                    {blockedMembers.map((member) => (
                      <div key={member.id} className="rounded-xl border border-red-100 bg-white p-4 shadow-sm" data-testid={`blocked-member-mobile-card-${member.id}`}>
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex items-center gap-3 min-w-0">
                            <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
                              <span className="text-sm font-medium text-red-500">
                                {member.name.charAt(0).toUpperCase()}
                              </span>
                            </div>
                            <div className="min-w-0">
                              <p className="font-medium text-gray-500 truncate">{member.name}</p>
                              <p className="text-sm text-gray-400 truncate inline-flex items-center gap-1">
                                <Mail className="w-3.5 h-3.5" />
                                {member.email}
                              </p>
                              <p className="text-sm text-gray-400 truncate inline-flex items-center gap-1 mt-1">
                                <Phone className="w-3.5 h-3.5" />
                                {member.phone || "-"}
                              </p>
                              <p className="text-sm mt-1">
                                <span className={getRoleLabel(member) === "Besitzer" ? "text-emerald-700" : getRoleLabel(member) === "Superadmin" ? "text-indigo-700" : getRoleLabel(member) === "Co-Admin" ? "text-blue-700" : "text-gray-500"}>
                                  {getRoleLabel(member)}
                                </span>
                              </p>
                            </div>
                          </div>
                          <Badge className="bg-red-50 text-red-500 border-red-200 whitespace-nowrap">
                            <ShieldOff className="w-3 h-3 mr-1" />
                            Blockiert
                          </Badge>
                        </div>
                        <div className="mt-3 flex justify-end gap-2">
                          {canManageCoAdmins ? (
                            <Button
                              variant="outline"
                              size="sm"
                              className="text-blue-600 border-blue-200 hover:bg-blue-50 hover:text-blue-700"
                              onClick={() => handleToggleCoAdmin(member)}
                              disabled={coAdminUpdatingUser === member.id || !canManageMember(member)}
                              data-testid={`coadmin-blocked-btn-mobile-${member.id}`}
                            >
                              {coAdminUpdatingUser === member.id ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                              ) : member.is_coadmin ? (
                                "Co-Admin entfernen"
                              ) : (
                                "Co-Admin"
                              )}
                            </Button>
                          ) : null}
                          <Button
                            variant="outline"
                            size="sm"
                            className="text-[#17c671] border-[#17c671]/30 hover:bg-[#17c671]/10"
                            onClick={() => handleBlockUser(member.id, false)}
                            disabled={blockingUser === member.id || !canManageMember(member)}
                            data-testid={`unblock-btn-mobile-${member.id}`}
                          >
                            {blockingUser === member.id ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <>
                                <Shield className="w-4 h-4 mr-1" />
                                Entsperren
                              </>
                            )}
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>
          )}
        </div>
      </main>
      <AppFooter />
    </div>
  );
};

export default TeamPage;

